import {
  inject,
  Injector,
  NgModule,
  runInInjectionContext,
} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateFn,
  RouterModule,
  RouterStateSnapshot,
  Routes,
  UrlTree,
} from '@angular/router';
import {
  AuthGuard,
  AuthGuardFn,
  CommunityAuthGuardFn,
} from './shared/components/auth.guard';
import { AboutUsComponent } from './modules/aboutus/aboutus.component';
import { AlliancesComponent } from './modules/alliances/alliances.component';
import { ComisionesComponent } from './modules/comisiones/comisiones.component';
import { ComisionDetailComponent } from './modules/comisiones/component/comision-detail/comision-detail.component';
import { CommitmentsComponent } from './modules/commitments/commitments.component';
import { CommitmentDetailComponent } from './modules/commitments/component/commitment-detail/commitment-detail.component';
import { CommunitiesCommitmentsComponent } from './modules/commitments/communities/communities.component';
import { CommunitiesComponent } from './modules/communities/communities.component';
import { NewsDetailComponent } from './modules/events/component/news-detail.component';
import { NewsComponent } from './modules/events/news.component';
import { FormularioComponent } from './modules/formulario/formulario.component';
import { HomeComponent } from './modules/home/home.component';
import { InvoicesPage } from './modules/invoices/invoices.page';
import { MarketComponent } from './modules/market/market.component';
import { PaymentsPage } from './modules/payments/payments.page';
import { PlataformComponent } from './modules/plataform/plataform.component';
import { ProyectDetailComponent } from './modules/proyects/component/proyect-detail.component';
import { ProyectsComponent } from './modules/proyects/proyects.component';
import { SincroniaComponent } from './modules/sincronia/sincronia.component';
import { ThankYouComponent } from './modules/thank-you/thank-you.component';
import { UserPage } from './modules/user/user.page';
import { CommunityPanelComponent } from './modules/communities/community-panel/community-panel.component';
import { CommunityCommitmentsComponent } from './modules/commitments/communities/community/community.component';
import {
  CommunitySuscriptionGuard,
  SuscriptionGuard,
} from './shared/components/suscription.guard';
import { concatMap, from, last, Observable, of, takeWhile } from 'rxjs';
import { PostulateProyectComponent } from './modules/proyects/postulate/postulate-proyect/postulate-proyect.component';
import { CommunityDataResolver } from './modules/communities/community-panel/community-panel.resolver';
import { IniciativaDetailComponent } from './modules/communities/community-panel/components/panel/iniciativas/details/iniciativa-detail.component';
import { CommunitiesResolver } from './modules/commitments/communities/communities.resolver';
import { CommunityCommitmentsResolver } from './modules/commitments/communities/community/community.resolver';
import { OrdersPage } from './modules/orders/orders.page';
import { HiddenMarketComponent } from './modules/hidden-market/hidden-market.component';
import { ImpactPanelPage } from './modules/impact-panel/impact-panel.page';
//import { UserPage } from './modules/user/user.page';
const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    component: HomeComponent,
  },
  {
    path: 'nosotros',
    component: AboutUsComponent,
  },
  {
    path: 'alianzas',
    component: AlliancesComponent,
  },
  {
    path: 'comisiones',
    component: ComisionesComponent,
    canActivate: [SequentialGuards([AuthGuardFn, SuscriptionGuard])],
  },
  {
    path: 'comisiones/:id',
    component: ComisionDetailComponent,
    canActivate: [SequentialGuards([AuthGuardFn, SuscriptionGuard])],
  },
  {
    path: 'compromisos',
    component: PlataformComponent,
  },
  {
    path: 'compromisos/:id',
    component: CommitmentsComponent,
  },
  {
    path: 'compromisos/:id/detalle',
    component: CommitmentDetailComponent,
  },
  {
    path: 'comunidades',
    component: CommunitiesComponent,
  },
  {
    path: 'comunidades/:id',
    component: CommunityPanelComponent,
    pathMatch: 'full',
    canActivate: [
      SequentialGuards([CommunityAuthGuardFn, CommunitySuscriptionGuard]),
    ],
    resolve: {
      communityData: CommunityDataResolver,
    },
  },
  {
    path: 'compromisos-comunidades',
    component: CommunitiesCommitmentsComponent,
    resolve: {
      communities: CommunitiesResolver,
    },
  },
  {
    path: 'compromisos-comunidades/:id',
    component: CommunityCommitmentsComponent,
    resolve: {
      community: CommunityCommitmentsResolver,
    },
  },
  {
    path: 'comunidades/:comunidad/iniciativas/:id',
    component: IniciativaDetailComponent,
    // canActivate: [SequentialGuards([AuthGuardFn, SuscriptionGuard])],
  },
  {
    path: 'contacto',
    component: FormularioComponent,
  },
  {
    path: 'facturas',
    component: InvoicesPage,
    canActivate: [SequentialGuards([AuthGuardFn, SuscriptionGuard])],
  },
  {
    path: 'unite',
    component: MarketComponent,
  },
  {
    path: 'activo-aliado',
    component: HiddenMarketComponent,
  },
  {
    path: 'novedades',
    component: NewsComponent,
  },
  {
    path: 'novedades/:id',
    component: NewsDetailComponent,
  },
  {
    path: 'pagos',
    component: PaymentsPage,
    canActivate: [SequentialGuards([AuthGuardFn, SuscriptionGuard])],
  },
  {
    path: 'ordenes',
    component: OrdersPage,
    canActivate: [SequentialGuards([AuthGuardFn, SuscriptionGuard])],
  },
  {
    path: 'proyectos',
    component: ProyectsComponent,
  },
  {
    path: 'proyectos/postulate',
    pathMatch: 'full',
    component: PostulateProyectComponent,
  },
  {
    path: 'proyectos/:id',
    component: ProyectDetailComponent,
  },
  {
    path: 'sincronia',
    component: SincroniaComponent,
  },
  {
    path: 'gracias',
    component: ThankYouComponent,
  },
  {
    path: 'usuario',
    component: UserPage,
    canActivate: [AuthGuardFn],
  },
  {
    path: 'panel-impacto',
    component: ImpactPanelPage,
    canActivate: [AuthGuardFn],
  },
];

export function SequentialGuards(guards: CanActivateFn[]): CanActivateFn {
  return (route, state) => {
    const injectionContext = inject(Injector);
    // Convert an array into an observable.
    return from(guards).pipe(
      // For each guard, fire canActivate and wait for it to complete.
      concatMap(guard => {
        return runInInjectionContext(injectionContext, () => {
          var guardResult = guard(route, state);
          if (guardResult instanceof Observable) {
            return guardResult;
          } else if (guardResult instanceof Promise) {
            return from(guardResult);
          } else {
            return of(guardResult);
          }
        });
      }),
      // Don't execute the next guard if the current guard's result is not true.
      takeWhile(value => value === true, true),
      // Return the last guard's result.
      last(),
    );
  };
}

@NgModule({
  declarations: [],
  imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'top' })],
  exports: [RouterModule],
  providers: [AuthGuard],
})
export class AppRoutingModule {}
